import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import DataTable from 'react-data-table-component';
import DataTableExtensions from 'react-data-table-component-extensions';
import { getOrders } from '../../Services/Dashboard';
import SpinnerModal from '../../Spinner/SpinnerModal';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import '../../UI/PatientList.css';

const toDateStr = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const computeRange = (key) => {
  const today = new Date();
  switch (key) {
    case 'all':
      return { startDate: '', endDate: '' };
    case 'yesterday': {
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      return { startDate: toDateStr(yesterday), endDate: toDateStr(yesterday) };
    }
    case 'lastweek': {
      const start = new Date(today);
      start.setDate(today.getDate() - 6);
      return { startDate: toDateStr(start), endDate: toDateStr(today) };
    }
    case 'month': {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      return { startDate: toDateStr(start), endDate: toDateStr(today) };
    }
    case 'today':
    default:
      return { startDate: toDateStr(today), endDate: toDateStr(today) };
  }
};

const PAGE_SIZE_OPTIONS = [10, 20, 30, 50];

const OrderManagement = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [activeFilter, setActiveFilter] = useState('today');
  const [dateRange, setDateRange] = useState(computeRange('today'));
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [totalCount, setTotalCount] = useState(0);
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);

  const fetchOrders = async (startDate, endDate, search, pageNumber = page, size = pageSize) => {
    try {
      setIsLoading(true);
      const LabID = sessionStorage.getItem('LabID');
      const data = await getOrders(LabID, startDate, endDate, search, pageNumber, size);
      setOrders(data?.items || []);
      setTotalCount(data?.totalCount || 0);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders(dateRange.startDate, dateRange.endDate, searchTerm, 1, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onQuickFilter = (key) => {
    setActiveFilter(key);
    const range = computeRange(key);
    setDateRange(range);
    setPage(1);
    setResetPaginationToggle((prev) => !prev);
    fetchOrders(range.startDate, range.endDate, searchTerm, 1, pageSize);
  };

  const onCustomDateChange = (e) => {
    const { name, value } = e.target;
    setDateRange((prev) => ({ ...prev, [name]: value }));
  };

  const onApplyCustomRange = () => {
    setActiveFilter('custom');
    setPage(1);
    setResetPaginationToggle((prev) => !prev);
    fetchOrders(dateRange.startDate, dateRange.endDate, searchTerm, 1, pageSize);
  };

  const onSearch = () => {
    setPage(1);
    setResetPaginationToggle((prev) => !prev);
    fetchOrders(dateRange.startDate, dateRange.endDate, searchTerm, 1, pageSize);
  };

  const onChangePage = (newPage) => {
    setPage(newPage);
    fetchOrders(dateRange.startDate, dateRange.endDate, searchTerm, newPage, pageSize);
  };

  const onChangeRowsPerPage = (newPageSize, newPage) => {
    setPageSize(newPageSize);
    setPage(newPage);
    fetchOrders(dateRange.startDate, dateRange.endDate, searchTerm, newPage, newPageSize);
  };

  const onSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      onSearch();
    }
  };

  const NavigatetoProfile = (id) => {
    navigate(`/Home/PatientProfile/${id}`);
  };

  const column = [
    {
      name: 'Order ID',
      selector: (row) => row.orderID,
      cellExport: (row) => row.orderID,
      sortable: true,
      width: '110px',
    },
    {
      name: 'Date',
      selector: (row) => row.createdAt,
      cellExport: (row) => row.createdAt,
      sortable: true,
      cell: (row) => (
        <span>{new Date(row.createdAt).toLocaleString()}</span>
      ),
    },
    {
      name: 'Patient Name',
      selector: (row) => row.patientName,
      cellExport: (row) => row.patientName,
      sortable: true,
      cell: (row) => (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span
            onClick={() => NavigatetoProfile(row.patientID)}
            style={{ cursor: 'pointer', color: '#0d6efd', fontWeight: 'bold' }}
          >
            {row.patientName?.toUpperCase()}
          </span>
          <span style={{ fontSize: '12px', color: 'gray' }}>
            {row.age} years | {row.sex}
          </span>
        </div>
      ),
    },
    {
      name: 'Contact Number',
      selector: (row) => row.contactNumber,
      cellExport: (row) => row.contactNumber,
      sortable: true,
    },
    {
      name: 'Total Amount',
      selector: (row) => row.totalAmount,
      cellExport: (row) => row.totalAmount,
      sortable: true,
      cell: (row) => <span>{Number(row.totalAmount || 0).toFixed(2)}</span>,
    },
  ];

  const tableData = {
    column,
    data: orders,
  };

  const customStyles = {
    rows: {
      style: {
        borderBottom: '1px solid #ccc',
      },
    },
    cells: {
      style: {
        fontSize: '12px',
      },
    },
    headCells: {
      style: {
        borderTop: '1px solid #ccc',
        backgroundColor: 'white',
      },
    },
  };

  return (
    <>
      {isLoading && <SpinnerModal isLoading={true} />}
      <div className="container-fluid Container2">
        <div className="Common-min-height pl-child-div common-box-shadow common-bg-white common-br-3px">
          <div style={{ padding: '5px' }} className="row">
            <div className="col-sm-12 col-md-6">
              <label className="common-Labels">Quick Filter</label>
              <div>
                <ButtonGroup className="quick-filter-group">
                  {[
                    { key: 'all', label: 'All' },
                    { key: 'today', label: 'Today' },
                    { key: 'yesterday', label: 'Yesterday' },
                    { key: 'lastweek', label: 'Last Week' },
                    { key: 'month', label: 'This Month' },
                  ].map(({ key, label }) => (
                    <Button
                      key={key}
                      variant="outlined"
                      onClick={() => onQuickFilter(key)}
                      className={`common-button common-FontFamily quick-filter-btn ${activeFilter === key ? 'quick-filter-btn-active' : ''}`}
                    >
                      {activeFilter === key && (
                        <CheckCircleRoundedIcon className="quick-filter-btn-icon" />
                      )}
                      {label}
                    </Button>
                  ))}
                </ButtonGroup>
              </div>
            </div>
            <div className="col-sm-12 col-md-6">
              <label className="common-Labels">Search by Patient Name</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  className="form-control common-Input"
                  type="text"
                  placeholder="Search patient name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={onSearchKeyDown}
                />
                <Button variant="contained" type="button" className="common-button" onClick={onSearch}>
                  Search
                </Button>
              </div>

              <div style={{ display: 'flex', gap: '8px', marginTop: '8px', alignItems: 'flex-end' }}>
                <div style={{ flex: 1 }}>
                  <label className="common-Labels">Date From</label>
                  <input
                    className="form-control common-Input"
                    name="startDate"
                    type="date"
                    onChange={onCustomDateChange}
                    value={dateRange.startDate}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label className="common-Labels">Date To</label>
                  <input
                    className="form-control common-Input"
                    name="endDate"
                    type="date"
                    onChange={onCustomDateChange}
                    value={dateRange.endDate}
                  />
                </div>
                <Button
                  variant="contained"
                  onClick={onApplyCustomRange}
                  type="button"
                  className="common-button"
                  style={{ marginBottom: '1px' }}
                >
                  Apply
                </Button>
              </div>
            </div>
          </div>
          <DataTableExtensions columns={tableData.column} data={tableData.data} filter={false} export={false} print={false}>
            <DataTable
              pagination
              paginationServer
              paginationTotalRows={totalCount}
              paginationDefaultPage={1}
              paginationResetDefaultPage={resetPaginationToggle}
              paginationPerPage={pageSize}
              paginationRowsPerPageOptions={PAGE_SIZE_OPTIONS}
              onChangePage={onChangePage}
              onChangeRowsPerPage={onChangeRowsPerPage}
              fixedHeader
              fixedHeaderScrollHeight="80vh"
              highlightOnHover
              pointerOnHover
              responsive
              striped
              customStyles={customStyles}
              onRowClicked={(row) => NavigatetoProfile(row.patientID)}
            />
          </DataTableExtensions>
        </div>
      </div>
    </>
  );
};

export default OrderManagement;
